import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { PROVIDERS } from "@/lib/providers";
import { policyWarnings, isRestricted } from "@/lib/policy";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useTaal } from "@/contexts/TaalContext";

/**
 * The routing policy, as a screen.
 *
 * Reads and writes GET/PUT /api/policy and nothing else. No new behaviour: the
 * rules were already enforced in services/orchestrator.py, they simply had no
 * way to be seen or changed short of a database row.
 *
 * The council's choice of local models is deliberately not here. It stays in
 * Advanced where it already lives.
 */
export default function RoutingPolicyPanel({ ollamaModels = [] }) {
  const { t } = useTaal();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/policy`)
      .then((res) => {
        if (!cancelled) setPolicy(res.data);
      })
      .catch(() => {
        // A policy that will not load must not read as "no restrictions".
        if (!cancelled) setPolicy(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = useCallback((field, value) => {
    setPolicy((prev) => {
      const list = prev[field] || [];
      return {
        ...prev,
        [field]: list.includes(value)
          ? list.filter((x) => x !== value)
          : [...list, value],
      };
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/policy`, {
        allowed_providers: policy.allowed_providers,
        allowed_models: policy.allowed_models,
        escalation_enabled: policy.escalation_enabled,
      });
      setPolicy(res.data);
      toast.success(t("Routeringsbeleid opgeslagen"));
    } catch (err) {
      toast.error(t("Het routeringsbeleid kon niet worden opgeslagen"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("Je routeringsbeleid wordt geladen")}
      </div>
    );
  }

  if (!policy) {
    return (
      <p className="text-sm text-muted-foreground py-8" data-testid="policy-load-failed">
        {t("Het routeringsbeleid kon niet worden geladen, dus het kan hier niet worden getoond of gewijzigd. Wat er is opgeslagen, wordt nog steeds toegepast.")}
      </p>
    );
  }

  // What each provider can answer with, as far as this screen knows. Ollama's
  // list comes from the machine; everyone else's from the catalog.
  const catalog = Object.fromEntries(
    Object.entries(PROVIDERS).map(([key, meta]) => [
      key,
      key === "ollama" ? ollamaModels : (meta.models || []).map((m) => m.value),
    ])
  );

  const warnings = policyWarnings(policy, catalog);
  const restricted = isRestricted(policy);

  return (
    <div className="space-y-4" data-testid="routing-policy-panel">
      {/* Escalation first: it is the only setting here that decides whether
          anything leaves the machine. */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="escalation-enabled" className="font-medium text-sm">
              {t("Moeilijke vragen mogen deze machine verlaten")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("Standaard uit, en uit is uit. Staat dit aan, dan gaat een vraag waar het lokale model naar verwachting slecht in is naar een cloudaanbieder, en dat betekent dat")}{" "}
              <strong>{t("je prompt deze machine verlaat")}</strong> {t("en bij een bedrijf komt waarvan je een API-sleutel hebt. Je wordt het elke keer eerst gevraagd.")}
            </p>
          </div>
          <Switch
            id="escalation-enabled"
            data-testid="escalation-toggle"
            checked={!!policy.escalation_enabled}
            onCheckedChange={(checked) =>
              setPolicy((prev) => ({ ...prev, escalation_enabled: checked }))
            }
          />
        </div>
        {!policy.escalation_enabled && (
          <p className="text-xs text-muted-foreground" data-testid="escalation-off-note">
            {t("Alles blijft op het lokale model.")}
          </p>
        )}
      </div>

      {/* The trap: an empty list allows everything. Said once, plainly, above
          both lists rather than in a footnote under them. */}
      <div className="flex gap-2 text-xs text-muted-foreground bg-muted rounded-lg p-3">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <p data-testid="allowlist-explanation">
          {restricted
            ? t("Alleen wat hieronder is aangevinkt mag worden gebruikt. Vink alles in een lijst uit om die beperking helemaal op te heffen.")
            : t("Er is niets aangevinkt, dus alles mag. Vink je iets aan, dan mag alleen nog wat in de lijst staat.")}
        </p>
      </div>

      {warnings.map((warning, i) => (
        <div
          key={i}
          data-testid={`policy-warning-${warning.level}`}
          className={`flex gap-2 text-xs rounded-lg p-3 ${
            warning.level === "error"
              ? "bg-destructive/10 text-destructive"
              : "bg-muted text-muted-foreground"
          }`}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <p>{warning.text}</p>
        </div>
      ))}

      <div className="border rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-sm">{t("Aanbieders")}</h4>
        <div className="space-y-1" data-testid="provider-allowlist">
          {Object.entries(PROVIDERS).map(([key, meta]) => (
            <label
              key={key}
              className="flex items-center gap-2 text-sm cursor-pointer py-0.5"
            >
              <input
                type="checkbox"
                checked={(policy.allowed_providers || []).includes(key)}
                onChange={() => toggle("allowed_providers", key)}
                className="h-4 w-4 rounded border-input accent-[hsl(var(--accent))]"
              />
              <span>{meta.name}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-sm">{t("Modellen")}</h4>
        <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="model-allowlist">
          {Object.entries(PROVIDERS).map(([key, meta]) => {
            const models = catalog[key] || [];
            if (!models.length) return null;
            return (
              <div key={key} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{meta.name}</p>
                {models.map((model) => (
                  <label
                    key={model}
                    className="flex items-center gap-2 text-sm cursor-pointer py-0.5 pl-2"
                  >
                    <input
                      type="checkbox"
                      checked={(policy.allowed_models || []).includes(model)}
                      onChange={() => toggle("allowed_models", model)}
                      className="h-4 w-4 rounded border-input accent-[hsl(var(--accent))]"
                    />
                    <span className="font-mono text-xs">{model}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
        {!ollamaModels.length && (
          <p className="text-xs text-muted-foreground">
            {t("Er staan geen lokale modellen in de lijst. Ollama moet draaien om ze hier te laten verschijnen.")}
          </p>
        )}
      </div>

      <Button onClick={save} disabled={saving} data-testid="save-policy">
        {saving ? t("Opslaan") : t("Routeringsbeleid opslaan")}
      </Button>
    </div>
  );
}
